import { useMemo } from 'react';
import { useApiQuery } from '../useApiQuery';
import { operationsClient, apiGet } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerBooking } from '@/types/scheduler';

/**
 * NOT-317 — bookings without a date/time (the Calendar's "Unscheduled" panel).
 *
 * These never come back through the date-range list (they have no `startAt`),
 * so we query the same way the Dispatch queue does: `statusGroup: 'unscheduled'`
 * (RESERVED + no slot, which already excludes cancelled). We still filter
 * defensively in case the projection shape shifts.
 */
export function useUnscheduledBookings() {
  const query = useApiQuery<{ items?: SchedulerBooking[] }>({
    queryKey: schedulerKeys.bookings({ statusGroup: 'unscheduled' }),
    queryFn: () =>
      apiGet<{ items?: SchedulerBooking[] }>(operationsClient, schedulerEndpoints.bookings(), {
        params: { statusGroup: 'unscheduled', limit: 100, offset: 0 },
      }),
    showErrorToast: false,
  });

  const bookings = useMemo(
    () => (query.data?.items ?? []).filter((b) => !b.startAt && b.status !== 'CANCELLED'),
    [query.data?.items],
  );

  return { bookings, count: bookings.length, isLoading: query.isLoading };
}
